import { COMPONENT_TYPES } from '@/models/enums/component-types.enum';
import { SurveyElementsUnion } from '@/models/survey-elements-union';
import { BUTTON_ELEMENT_ACTION_TYPES } from '@/models/enums/button-element-action-types.enum';
import store from '../store';
import { TextfieldValue } from '@/models/survey-elements/textfield-value';
import { SurveyGroupingElement } from '@/models/survey-elements/grouping-element';

const COMPONENTS_WITH_SELF_SUBMIT = [
  COMPONENT_TYPES.RATING_5_STARS_ELEMENT,
  COMPONENT_TYPES.SINGLE_SELECT_ELEMENT,
  COMPONENT_TYPES.SINGLE_BUTTON_SELECT_ELEMENT,
  COMPONENT_TYPES.TEXT_FIELD_ELEMENT,
];

export default class PageFieldsManager {
  private pageFields: SurveyElementsUnion[] = [];
  private pageFieldsTotalCount: number = 0;
  private isNextView: boolean = false;
  private surveyId: string = '';
  private showSubmitButton: boolean = false;

  constructor(
    elements: SurveyElementsUnion[],
    isNextView: boolean,
    showSubmitButton: boolean,
    surveyId: string
  ) {
    this.isNextView = isNextView;
    this.showSubmitButton = showSubmitButton;
    this.surveyId = surveyId;
    this.pageFieldsTotalCount = elements.filter(
      item => item.isAnswerable
    ).length;
    this.pageFields = this.setPageFields(elements);
  }

  private setPageFields(
    elements: SurveyElementsUnion[]
  ): SurveyElementsUnion[] {
    return elements!
      .reduce((results: SurveyElementsUnion[], item: SurveyElementsUnion) => {
        const isComponentValid: boolean = Object.values(
          COMPONENT_TYPES
        ).includes(item.elementType!);
        if (isComponentValid) {
          if (item.elementType === COMPONENT_TYPES.GROUPING_ELEMENT) {
            const groupingElement = item as SurveyGroupingElement;
            groupingElement.elements = groupingElement.elements.map(element => {
              return {
                ...element,
                ...this.assignDefaultValue(element),
              };
            });
          }
          results.push({
            ...item,
            ...this.assignDefaultValue(item),
          });
        }
        return results;
      }, [])
      .sort((a, b) => a.order - b.order);
  }

  getPageFields(): SurveyElementsUnion[] {
    return this.pageFields;
  }

  private assignDefaultValue({
    isAnswerable,
    elementType,
  }: SurveyElementsUnion) {
    if (!isAnswerable) return {};
    if (
      elementType === COMPONENT_TYPES.SELECT_FACILITY_ELEMENT &&
      store.state.defaultFacility
    ) {
      return { value: store.state.defaultFacility.id.toString() };
    }
    return { value: null };
  }

  hasAnswerableFields(): boolean {
    return this.pageFields.some(({ isAnswerable }) => isAnswerable);
  }

  getPageFieldsPayload() {
    const answerableFields = this.pageFields.filter(
      ({ isAnswerable }) => isAnswerable
    );

    const groupingElement = this.pageFields.find(
      ({ elementType }) => elementType === COMPONENT_TYPES.GROUPING_ELEMENT
    ) as SurveyGroupingElement | undefined;
    const nestedAnswerableFields = answerableFields.reduce(
      (results: SurveyElementsUnion[], item: SurveyElementsUnion) => {
        if (
          'additionalTextAreaElements' in item &&
          item.additionalTextAreaElements.filter(({ value }) => value).length
        ) {
          results.push(...item.additionalTextAreaElements!);
        }
        return results;
      },
      []
    );
    const groupedElements =
      groupingElement?.elements.reduce(
        (results: SurveyElementsUnion[], item: SurveyElementsUnion) => {
          if (item.isAnswerable) {
            results.push(item!);
          }
          return results;
        },
        []
      ) || [];

    const fields = [
      ...answerableFields,
      ...nestedAnswerableFields,
      ...groupedElements,
    ];
    return fields
      .filter(({ value, isRequired }) => {
        return value !== null || isRequired;
      })
      .map(({ value, id: question, elementType }: SurveyElementsUnion) => {
        return {
          survey: this.surveyId,
          question,
          value: this.getPayloadValue(elementType!, value),
        };
      });
  }

  private getPayloadValue(elementType: COMPONENT_TYPES, value: any) {
    if (!value) return value;
    const fields: { [key in COMPONENT_TYPES]?: any } = {
      [COMPONENT_TYPES.CHECKBOX_ELEMENT]: { value },
      [COMPONENT_TYPES.RATING_5_STARS_ELEMENT]: value,
      [COMPONENT_TYPES.SINGLE_SELECT_ELEMENT]: value,
      [COMPONENT_TYPES.SINGLE_BUTTON_SELECT_ELEMENT]: value,
      [COMPONENT_TYPES.MULTIPLE_SELECT_ELEMENT]: value,
      [COMPONENT_TYPES.SELECT_FACILITY_ELEMENT]: { id: value },
    };

    const defaultResponse = { text: value };
    if (!elementType || !fields[elementType]) {
      return defaultResponse;
    }
    return fields[elementType];
  }

  isSessionFinished(): boolean {
    return (
      !this.isNextView &&
      !this.pageFields.filter(item => {
        return (
          item.elementType === COMPONENT_TYPES.BUTTON &&
          'action' in item &&
          item.action !== BUTTON_ELEMENT_ACTION_TYPES.GO_TO_MAPS
        );
      }).length
    );
  }
}
